import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserGroup, 
  faBuilding, 
  faBuilding as faBuilding2,
  faCar,
  faLayerGroup,
  faUser,
  faBox,
  faScaleBalanced
} from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { BASE_URL } from '../utils/constants';

// Icono de mascota personalizado
const PetIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 3C7.12 3 6 4.12 6 5.5S7.12 8 8.5 8 11 6.88 11 5.5 9.88 3 8.5 3zm7 0C14.12 3 13 4.12 13 5.5S14.12 8 15.5 8 18 6.88 18 5.5 16.88 3 15.5 3zM3 9.25c-1.24 0-2.25 1.01-2.25 2.25S1.76 13.75 3 13.75 5.25 12.74 5.25 11.5 4.24 9.25 3 9.25zm18 0c-1.24 0-2.25 1.01-2.25 2.25s1.01 2.25 2.25 2.25S23.25 12.74 23.25 11.5 22.24 9.25 21 9.25zM12 10c-3.31 0-6 2.69-6 6 0 1.66.67 3.16 1.76 4.24l1.42-1.42C8.45 18.09 8 17.1 8 16c0-2.21 1.79-4 4-4s4 1.79 4 4c0 1.1-.45 2.09-1.18 2.82l1.42 1.42C17.33 19.16 18 17.66 18 16c0-3.31-2.69-6-6-6z"/>
  </svg>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [packageStats, setPackageStats] = useState({ received: 0, delivered: 0, pending: 0 });
  const [visitStats, setVisitStats] = useState({ registered: 0, pending: 0 });

  useEffect(() => {
    // Obtener estadísticas de paquetes (últimos 30 días) usando el endpoint del backend
    axios.get(`${BASE_URL}/packageStats`)
      .then(response => {
        const data = response.data;
        setPackageStats({ 
          received: data.total_paquetes || 0, 
          delivered: data.paquetes_entregados || 0, 
          pending: data.paquetes_pendientes || 0 
        });
      })
      .catch(error => {
        console.error('Error fetching package stats:', error);
        setPackageStats({ received: 0, delivered: 0, pending: 0 });
      });

    // Obtener estadísticas de visitas (últimos 7 días) usando el endpoint del backend
    axios.get(`${BASE_URL}/visitStats`)
      .then(response => {
        const data = response.data;
        const totalVisitas = data.total_visitas || 0;
        const completadas = data.visitas_completadas || 0;
        const pendientes = totalVisitas - completadas;
        setVisitStats({ 
          registered: completadas, 
          pending: pendientes 
        });
      })
      .catch(error => {
        console.error('Error fetching visit stats:', error);
        setVisitStats({ registered: 0, pending: 0 });
      });
  }, []);

  const adminCards = [
    {
      title: 'USUARIOS',
      description: 'Administra los usuarios del sistema.',
      icon: faUserGroup,
      gradient: 'from-emerald-500 to-teal-600',
      route: '/adminUsers'
    },
    {
      title: 'APARTAMENTOS',
      description: 'Administra los apartamentos del conjunto.',
      icon: faBuilding,
      gradient: 'from-green-500 to-emerald-600',
      route: '/adminApartments'
    },
    {
      title: 'TORRES',
      description: 'Administra las torres del conjunto.',
      icon: faBuilding2,
      gradient: 'from-teal-500 to-cyan-600',
      route: '/adminTowers'
    },
    {
      title: 'VEHICULOS',
      description: 'Administra los vehículos del conjunto.',
      icon: faCar,
      gradient: 'from-lime-500 to-green-600',
      route: '/adminVehicles'
    },
    {
      title: 'PARQUEADEROS',
      description: 'Administra los parqueaderos del conjunto.',
      icon: faLayerGroup,
      gradient: 'from-purple-500 to-indigo-600',
      route: '/adminParkings'
    },
    {
      title: 'PARQUEADEROS VISITANTES',
      description: 'Administra espacios para visitantes.',
      icon: faLayerGroup,
      gradient: 'from-indigo-500 to-purple-600',
      route: '/adminVisitorParking'
    },
    {
      title: 'MASCOTAS',
      description: 'Administra las mascotas del conjunto.',
      icon: PetIcon,
      gradient: 'from-emerald-600 to-green-700',
      route: '/adminPets'
    },
    {
      title: 'VISITANTES RECURRENTES',
      description: 'Gestiona visitantes con acceso frecuente.',
      icon: faUserGroup,
      gradient: 'from-indigo-500 to-blue-600',
      route: '/adminRecurringVisitors'
    },
    {
      title: 'CARTERA',
      description: 'Seguimiento de pagos y restricciones por mora.',
      icon: faScaleBalanced,
      gradient: 'from-amber-500 to-orange-600',
      route: '/adminCartera'
    }
  ];

  const reportCards = [
    {
      title: 'VISITAS',
      description: 'Visualiza las visitas al conjunto.',
      icon: faUser,
      gradient: 'from-primary-500 to-primary-700',
      route: '/visitReport'
    },
    {
      title: 'PAQUETERIA',
      description: 'Visualiza la paqueteria recibida.',
      icon: faBox,
      gradient: 'from-primary-600 to-primary-800',
      route: '/shipmentReport'
    }
  ];

  const packageData = [
    { name: 'Recibidos', value: packageStats.received, color: '#10b981' },
    { name: 'Entregados', value: packageStats.delivered, color: '#297B31' },
    { name: 'Pendientes', value: packageStats.pending, color: '#f59e0b' }
  ];

  const visitData = [
    { name: 'Con Ingreso', value: visitStats.registered, color: '#297B31' },
    { name: 'Pendientes', value: visitStats.pending, color: '#ef4444' }
  ];

  return (
    <>
      <Navigation/>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Estadísticas Section */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-800 text-center mb-8">
              Estadísticas
            </h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Gráfica de Paquetes */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-1 text-center">
                  Estado de Paquetería
                </h2>
                <p className="text-sm text-gray-500 mb-4 text-center">Últimos 30 días</p>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={packageData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {packageData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-100 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Recibidos</p>
                    <p className="text-2xl font-bold text-green-600">{packageStats.received}</p>
                  </div>
                  <div className="bg-primary-100 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Entregados</p>
                    <p className="text-2xl font-bold text-primary-600">{packageStats.delivered}</p>
                  </div>
                  <div className="bg-amber-100 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Pendientes</p>
                    <p className="text-2xl font-bold text-amber-600">{packageStats.pending}</p>
                  </div>
                </div>
              </div>

              {/* Gráfica de Visitas */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-1 text-center">
                  Estado de Visitas
                </h2>
                <p className="text-sm text-gray-500 mb-4 text-center">Últimos 7 días</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={visitData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                      {visitData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                  <div className="bg-primary-100 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Con Ingreso</p>
                    <p className="text-2xl font-bold text-primary-600">{visitStats.registered}</p>
                  </div>
                  <div className="bg-red-100 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Pendientes</p>
                    <p className="text-2xl font-bold text-red-600">{visitStats.pending}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Administración Section */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-800 text-center mb-8">
              Administración
            </h1>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {adminCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <div
                    key={index}
                    onClick={() => navigate(card.route)}
                    className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden cursor-pointer"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    <div className="p-4">
                      <div className="flex justify-center mb-3">
                        <div className={`p-2 rounded-lg bg-gradient-to-br ${card.gradient} shadow-md transform group-hover:scale-110 transition-transform duration-300`}>
                          {typeof Icon === 'function' ? (
                            <Icon className="h-6 w-6 text-white" />
                          ) : (
                            <FontAwesomeIcon icon={Icon} className="h-6 w-6 text-white" />
                          )}
                        </div>
                      </div>
                      
                      <h3 className="text-sm font-bold text-gray-800 text-center group-hover:text-primary-600 transition-colors">
                        {card.title}
                      </h3>
                    </div>
                    
                    <div className={`h-1 w-0 group-hover:w-full bg-gradient-to-r ${card.gradient} transition-all duration-500`}></div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reportes Section */}
          <div>
            <h1 className="text-4xl font-bold text-gray-800 text-center mb-8">
              Reportes
            </h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {reportCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <div
                    key={index}
                    onClick={() => navigate(card.route)}
                    className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden cursor-pointer"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-3">
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${card.gradient} shadow-md transform group-hover:scale-110 transition-transform duration-300`}>
                          <FontAwesomeIcon icon={Icon} className="h-7 w-7 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 group-hover:text-primary-600 transition-colors">
                          {card.title}
                        </h3>
                      </div>
                      
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                    
                    <div className={`h-1 w-0 group-hover:w-full bg-gradient-to-r ${card.gradient} transition-all duration-500`}></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;

